'use client';

import styles from './AdminChatContent.module.css';
import { getDateFromInputs, get30SecondsFromNow } from '@/_utils/dates';
import {
  updateConversation,
  generateResponse,
  editScheduledMessage,
  deleteMessage,
} from '@/_api/admin-conversations';
import type { AdminChatContentProps } from './AdminChatContent.types';
import type { Conversation, Message } from '@/_types/models/conversations';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Dropdown,
  Flex,
  InlineUnsafe,
  TrixEditor,
  ResultListItem,
  UploadBox,
  Toggle,
  Tooltip,
  Text,
} from '@/_components';
import { AdminChatMessage } from './components/AdminChatMessage';
import { AdminChatThreadAside } from './components/AdminChatThreadAside';
import { MessageDialog } from './components/MessageDialog';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';
import { DOCUMENT_ANSWER_PREFIX } from '@/_constants/documents';
import { QUESTION_SEPARATOR } from '@/_constants/questions';
import { UserContext } from '@/_contexts/UserProvider';
import { uploadToStorage } from '@/_api/client-documents';
import { useChatScroll } from '@/_hooks/useChatScroll';
import {
  useEffect,
  useCallback,
  useState,
  useContext,
  type FC,
  useMemo,
} from 'react';
import { validateFile } from '@/_utils/files';
import { ToastContext } from '@/_contexts/ToastProvider';
import { removeHtml } from '@/_utils/sanitize';
import { ScheduledMessageDialog } from './components/ScheduledMessageDialog';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { MESSAGE_TAGS, MESSAGE_TAG_OPTIONS } from '@/_constants/message-tags';
import { MessageTagType } from '@/_types/messages';
import { getClasses } from '@/_utils/classnames';
import { CONVERSATION_MESSAGE_LIMIT } from '@/_constants/conversations';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { ParticipantList } from './components/ParticipantList';
import { UnreadThreadIndicator } from './components/UnreadThreadIndicator';
import { useCurrentTime } from '@/_hooks/useCurrentTime';

export const AdminChatContent: FC<AdminChatContentProps> = ({ isPreview }) => {
  const { user, token, isUserReadOnly, isCompanyReadOnly } =
    useContext(UserContext);
  const {
    isLoading,
    isMessagesLoading,
    isSending,
    sendMessage,
    reloadConversations,
    selectedMessage: sMessage,
    setSelectedMessage: setSMessage,
    reloadMessages,
    setThreadSeen,
  } = useContext(AdminConversationsContext);
  const { addToast } = useContext(ToastContext);
  const { previewConversation, previewUser } = useContext(UserPreviewContext);
  const { selectedCompany } = useContext(CompanyContext);
  const { preferences } = useContext(FirmSettingsContext);

  const currentTime = useCurrentTime();

  const [hasReminder, setHasReminder] = useState<boolean>(false);
  const [isGeneratingResponse, setIsGeneratingResponse] =
    useState<boolean>(false);

  const companyId = selectedCompany?.id;

  const userId: number | undefined = useMemo(
    () => (isPreview ? previewUser?.id : user?.id),
    [isPreview, user, previewUser],
  );

  const isReadOnly: boolean = isUserReadOnly || isCompanyReadOnly;

  const conversation: Conversation | undefined = useMemo(
    () => (isPreview ? previewConversation : sMessage),
    [isPreview, sMessage, previewConversation],
  );

  const messagesContainer = useChatScroll(conversation);

  const [isScheduledMessageLoading, setScheduledMessageLoading] =
    useState<boolean>(false);
  const [isUploading, setUploading] = useState<boolean>(false);
  const [isAttaching, setAttaching] = useState<boolean>(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | undefined>(
    undefined,
  );
  const [messageTag, setMessageTag] = useState<MessageTagType>('general');
  const [showScheduledMessage, setScheduledMessage] = useState<boolean>(false);
  const [alertInfoLoading, setAlertInfoLoading] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [isEditMessageOpen, setIsEditMessageOpen] = useState<boolean>(false);
  const [isEditScheduleTimeOpen, setIsEditScheduleTimeOpen] =
    useState<boolean>(false);

  const onCancelScheduleMessage = () => setScheduledMessage(false);
  const onTagChange = (e: ResultListItem) => {
    setMessageTag(e.value as MessageTagType);
  };

  const onSendReply = async () => {
    const messageToSend = value;
    setValue('');

    await sendMessage(
      messageToSend,
      selectedMessage?.id,
      '',
      messageTag,
      hasReminder,
    );

    setMessageTag('general');
  };

  const onSelectReply = (message: Message) => {
    setThreadSeen(sMessage?.id!, message.id);
    setSelectedMessage(message);
  };

  const onCloseThread = () => setSelectedMessage(undefined);

  const {
    topLevelMessages,
    hasPreviousMessages,
    previousPageMessageCount,
    nextPageMessageCount,
    isAtBeginning,
  } = useMemo(() => {
    // Find messages that are not part of a thread
    const trimmedMessagesArray: Array<Message> =
      conversation?.messages?.filter?.((m: Message) => !m.message_thread_id) ||
      [];

    const totalMessages: number = trimmedMessagesArray.length;
    // Calculate start and end indices for pagination
    const startIdx: number = Math.max(
      totalMessages - (page + 1) * CONVERSATION_MESSAGE_LIMIT,
      0,
    );
    const endIdx: number = totalMessages - page * CONVERSATION_MESSAGE_LIMIT;

    // Slice the messages array to get the current page of messages
    const slicedMessages = trimmedMessagesArray.slice(startIdx, endIdx);

    const hasPrevious = startIdx > 0;
    const atBeginning = !hasPrevious && totalMessages > 0 && page > 0;

    // Calculate how many messages will be on the previous page
    const prevStartIdx = Math.max(
      totalMessages - (page + 2) * CONVERSATION_MESSAGE_LIMIT,
      0,
    );
    const prevEndIdx = Math.max(
      totalMessages - (page + 1) * CONVERSATION_MESSAGE_LIMIT,
      0,
    );
    const prevPageCount = prevEndIdx - prevStartIdx;

    // Calculate how many messages will be on the next page (current page - 1)
    const nextStartIdx = Math.max(
      totalMessages - page * CONVERSATION_MESSAGE_LIMIT,
      0,
    );
    const nextEndIdx =
      totalMessages - Math.max(page - 1, 0) * CONVERSATION_MESSAGE_LIMIT;
    const nextPageCount = Math.min(
      nextEndIdx - nextStartIdx,
      totalMessages - nextStartIdx,
    );

    return {
      topLevelMessages: slicedMessages,
      hasPreviousMessages: hasPrevious,
      previousPageMessageCount: prevPageCount,
      nextPageMessageCount: page > 0 ? nextPageCount : 0,
      isAtBeginning: atBeginning,
    };
  }, [conversation?.messages, page]);

  const hasNextMessages: boolean = page !== 0;

  const isInactiveConversation: boolean =
    conversation?.relevant_company_ids?.length === 0;

  const threadLevelMessages: Array<Message> = useMemo(() => {
    return (
      conversation?.messages?.filter?.(
        (m: Message) => m.message_thread_id === selectedMessage?.id,
      ) || []
    );
  }, [conversation?.messages, selectedMessage?.id]);

  const onOpenAttaching = () => setAttaching(true);
  const onCloseAttaching = () => setAttaching(false);

  const onDrop = useCallback(
    async <T extends File>(acceptedFiles: T[]) => {
      setUploading(true);
      let newValue: string = DOCUMENT_ANSWER_PREFIX;

      for (const file of acceptedFiles) {
        const { isValid } = await validateFile(file);

        if (!isValid) {
          addToast(
            'error',
            'We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.',
          );
          continue;
        }

        if (!companyId) {
          addToast(
            'error',
            'We were unable to upload the file, please try refreshing the page',
          );
          return;
        }

        // TODO: Fix this. Not working properly
        // if (isProtectedPDF) {
        //   addToast(
        //     'error',
        //     'The PDFs you uploaded are either password-protected or cannot be opened. Please either remove the password or upload alternative files.',
        //   );
        //   continue;
        // }

        const uploaded: any = await uploadToStorage({
          file,
          companyId: companyId!,
          path: '/Attachments',
          token,
        });

        if (uploaded?.error) {
          addToast('error', uploaded.error);
          continue;
        }

        if (newValue) {
          newValue += `${QUESTION_SEPARATOR}`;
        }

        newValue += `${uploaded?.document_location}`;
      }

      // only send message if there are attachments
      if (newValue !== DOCUMENT_ANSWER_PREFIX) {
        await sendMessage(newValue, selectedMessage?.id);
      }

      setUploading(false);
      setAttaching(false);
    },
    [token, companyId, selectedMessage?.id, sendMessage, addToast],
  );

  const onAddYourself = async () => {
    const newAdvisorIds: Array<number> = [
      ...(conversation?.advisor_ids || []),
      user?.id!,
    ];

    try {
      setAlertInfoLoading(true);
      await updateConversation({
        token,
        conversationId: conversation?.id!,
        advisor_ids: newAdvisorIds,
      });
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setAlertInfoLoading(false);
      setSMessage({
        ...conversation,
        advisor_ids: newAdvisorIds,
      } as Conversation);
      reloadConversations(true);
    }
  };

  const hasMessages: boolean = useMemo(
    () => !isMessagesLoading && !!topLevelMessages?.length,
    [isMessagesLoading, topLevelMessages?.length],
  );

  const showNotInConversation: boolean = Boolean(
    !isPreview &&
      !isMessagesLoading &&
      !isLoading &&
      !conversation?.advisor_ids?.includes(user?.id!),
  );

  const hasOOOAdvisor: boolean = useMemo(() => {
    return (
      !isPreview &&
      !isMessagesLoading &&
      !!conversation &&
      !!conversation.advisor_out_of_office
    );
  }, [isMessagesLoading, isPreview, conversation]);

  const showOOOMessage: boolean =
    !showNotInConversation && !isInactiveConversation && hasOOOAdvisor;

  const sanitizedValue = useMemo(() => removeHtml(value), [value]);

  const onScheduleMessage = () => setScheduledMessage(true);

  const onConfirmSchedule = async (date: string, time: string) => {
    try {
      setScheduledMessageLoading(true);
      const formatted = getDateFromInputs(date, time);

      await sendMessage(value, selectedMessage?.id, formatted);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setScheduledMessage(false);
      setValue('');
      setScheduledMessageLoading(false);
    }
  };

  const onSendAIResponse = async (message: Message) => {
    try {
      const formatted = get30SecondsFromNow();
      await editScheduledMessage({
        scheduled_time: formatted,
        message_id: message?.id!,
        token,
      });
      addToast('success', 'Draft message sent');
      reloadMessages(true);
    } catch (error) {
      addToast('error', 'Failed to send draft response', {
        error,
      });
    }
  };

  const onDeleteMessage = async (message: Message) => {
    try {
      const justCreated =
        message?.created_at &&
        new Date(message.created_at) >= new Date(currentTime - 30000);
      if (message?.scheduled) {
        const confirmed = confirm(
          'Are you sure you want to delete this message?',
        );
        if (!confirmed) return;
      } else if (!justCreated) {
        const confirmed = confirm(
          'Are you sure you want to delete this message? The email/sms has already been sent.',
        );
        if (!confirmed) return;
      }

      await deleteMessage({ message_id: message?.id!, token: token! });
      addToast(
        'success',
        `Message ${justCreated && !message?.scheduled ? 'unsent' : 'deleted'} successfully`,
      );
    } catch (error) {
      console.error(error);
    } finally {
      reloadMessages(true);
    }
  };

  const onGenerateResponse = async () => {
    try {
      setIsGeneratingResponse(true);

      const response = await generateResponse({
        conversationId: conversation?.id!,
        token: token!,
      });

      if ('message' in response) {
        setValue(response.message as string);
      } else {
        addToast('error', 'Failed to generate response', {
          error: response.error,
        });
      }
    } catch (error) {
      addToast('error', 'Failed to generate response', {
        error,
      });
    } finally {
      setIsGeneratingResponse(false);
    }
  };

  const messageFooterClasses: string = getClasses(styles.chatContent__footer, {
    [styles.chatContent__footer__thread]: !!selectedMessage?.id,
  });

  const onNextPage = () => setPage(page - 1);
  const onPreviousPage = () => setPage(page + 1);

  const handleEditMessage = (message: Message) => {
    setEditingMessage(message);
    setIsEditMessageOpen(true);
  };

  const handleEditScheduleTime = (message: Message) => {
    setEditingMessage(message);
    setIsEditScheduleTimeOpen(true);
  };

  useEffect(() => {
    messagesContainer?.current?.scrollIntoView({
      behavior: 'auto',
      block: 'end',
    });

    reloadMessages(true);
  }, [messagesContainer, reloadMessages]);

  const chatContainerClasses: string = getClasses(
    styles.chatContent__container,
  );

  const chatContainerPlaceholderClasses: string = getClasses(
    styles.chatContent__containerPlaceholder,
  );

  const messageEditorDisabled: boolean = useMemo(() => {
    return (
      isReadOnly || isPreview || isInactiveConversation || isMessagesLoading
    );
  }, [isReadOnly, isPreview, isInactiveConversation, isMessagesLoading]);

  return (
    <>
      <MessageDialog
        message={editingMessage}
        isEditMessageOpen={isEditMessageOpen}
        isEditScheduleTimeOpen={isEditScheduleTimeOpen}
        onCloseEditMessage={() => setIsEditMessageOpen(false)}
        onCloseEditScheduleTime={() => setIsEditScheduleTimeOpen(false)}
      />
      <ScheduledMessageDialog
        isOpen={showScheduledMessage}
        isLoading={isScheduledMessageLoading}
        onClose={onCancelScheduleMessage}
        onConfirm={onConfirmSchedule}
      />
      <Dialog
        isOpen={isAttaching}
        onClose={onCloseAttaching}
        title="Upload Documents"
      >
        <Dialog.Content>
          <UploadBox
            onDrop={onDrop}
            isLoading={isUploading}
            allowMultiple={true}
          />
        </Dialog.Content>
      </Dialog>
      {selectedMessage && (
        <AdminChatThreadAside onClose={onCloseThread}>
          <Flex isColumn={true} gap={6}>
            <AdminChatMessage
              isActive={true}
              isPreview={!!isPreview}
              isReply={true}
              onSelectReply={onSelectReply}
              isMe={selectedMessage?.user_id === userId}
              message={selectedMessage}
              currentTime={currentTime}
            />
            {threadLevelMessages?.map((message: Message, key) => {
              return (
                <AdminChatMessage
                  onSelectReply={onSelectReply}
                  isActive={false}
                  isReply={true}
                  isPreview={!!isPreview}
                  isMe={message?.user_id === userId}
                  message={message}
                  onEditMessage={handleEditMessage}
                  onEditScheduleTime={handleEditScheduleTime}
                  currentTime={currentTime}
                  key={key}
                />
              );
            })}
          </Flex>
        </AdminChatThreadAside>
      )}
      <Flex isColumn={true} className={styles.chatContent}>
        <div className={chatContainerClasses} ref={messagesContainer}>
          {hasPreviousMessages && (
            <Flex padding={8} isCentered={true}>
              <ButtonGroup>
                <Button
                  value={`Previous ${previousPageMessageCount} messages`}
                  iconLeft="arrow-up"
                  intent="secondary"
                  size="small"
                  onClick={onPreviousPage}
                />
              </ButtonGroup>
            </Flex>
          )}
          {isAtBeginning && (
            <Flex padding={8} isCentered={true}>
              <Text type="small" color="disabled">
                No more previous messages
              </Text>
            </Flex>
          )}
          {topLevelMessages?.map((message: Message, key: number) => {
            const unreadThreadMessageCount: number =
              (
                conversation?.messages?.filter?.(
                  (m: Message) => m.message_thread_id === message?.id,
                ) || []
              )?.reduce(
                (count, message) =>
                  message.unseen_user_ids.includes(user?.id!)
                    ? count + 1
                    : count,
                0,
              ) ?? 0;

            return (
              <AdminChatMessage
                isMe={message?.user_id === userId}
                isReply={false}
                isActive={false}
                isPreview={!!isPreview}
                unreadThreadMessageCount={unreadThreadMessageCount}
                onSelectReply={onSelectReply}
                onEditMessage={handleEditMessage}
                onEditScheduleTime={handleEditScheduleTime}
                onSendAIResponse={onSendAIResponse}
                onDeleteMessage={onDeleteMessage}
                message={message}
                currentTime={currentTime}
                key={key}
              />
            );
          })}
          {!hasMessages && <div className={chatContainerPlaceholderClasses} />}
          {isInactiveConversation && (
            <Alert type="warning">
              This conversation is archived because a new conversation has been
              created with different users. Messaging is disabled, please use
              the other conversation.
            </Alert>
          )}
          {showNotInConversation && (
            <Alert
              type="info"
              actions={
                <ButtonGroup align="right">
                  <Button
                    value="Add yourself"
                    size="small"
                    isDisabled={alertInfoLoading}
                    isLoading={alertInfoLoading}
                    onClick={onAddYourself}
                    intent="secondary"
                  />
                </ButtonGroup>
              }
            >
              You are not in this conversation.
            </Alert>
          )}
          {showOOOMessage && (
            <Alert type="info">
              <InlineUnsafe
                text={conversation?.advisor_out_of_office_message}
              />
            </Alert>
          )}
          {hasNextMessages && (
            <Flex padding={8} isCentered={true}>
              <ButtonGroup>
                <Button
                  value={`Latest ${nextPageMessageCount} messages`}
                  iconLeft="arrow-down"
                  intent="secondary"
                  size="small"
                  onClick={onNextPage}
                />
              </ButtonGroup>
            </Flex>
          )}
        </div>
        <UnreadThreadIndicator conversation={conversation} />
        {!isPreview && <ParticipantList conversation={conversation} />}
        <footer className={messageFooterClasses}>
          <TrixEditor
            value={value}
            isResponsive={true}
            setValue={setValue}
            isDisabled={messageEditorDisabled}
          />
          <div className={styles.chatContent__footer__buttonGroup}>
            <ButtonGroup>
              <Tooltip placement="bottom">
                <Tooltip.Trigger>
                  <Toggle
                    label="Auto-remind"
                    isChecked={hasReminder}
                    onChange={() => setHasReminder(!hasReminder)}
                    isSmall={true}
                  />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <Text type="tiny" color="currentcolor">
                    Client will receive a reminder every 3 days for a month if
                    not they do not reply. Any new messages will remove the
                    reminder.
                  </Text>
                </Tooltip.Content>
              </Tooltip>
              {!preferences?.message_tags_disabled && (
                <Dropdown
                  isDisabled={isPreview}
                  items={MESSAGE_TAG_OPTIONS}
                  onSelect={(e) => onTagChange(e)}
                  placement="top-end"
                >
                  <Button
                    value={MESSAGE_TAGS[messageTag]}
                    iconRight="caret-down"
                    intent="ghost"
                    isDisabled={isPreview}
                    size="small"
                  />
                </Dropdown>
              )}
              <Button
                intent="secondary"
                isIconOnly={true}
                iconLeft="calendar"
                size="tiny"
                isLoading={isUploading}
                isDisabled={isReadOnly || !sanitizedValue || isPreview}
                value="Schedule"
                withTooltip={true}
                onClick={onScheduleMessage}
              />
              <Button
                intent="secondary"
                isIconOnly={true}
                iconLeft="upload"
                size="tiny"
                isLoading={isUploading}
                isDisabled={isReadOnly || isPreview}
                value="Upload"
                withTooltip={true}
                onClick={onOpenAttaching}
              />
              <Button
                intent="secondary"
                isIconOnly={true}
                iconLeft="sparkle"
                size="tiny"
                isDisabled={isReadOnly || isPreview}
                value="AI response"
                onClick={onGenerateResponse}
                withTooltip={true}
                tooltip="Generate a response using AI"
                isLoading={isGeneratingResponse}
              />
              <Button
                intent="primary"
                iconLeft="send"
                size="small"
                isDisabled={
                  isSending || isReadOnly || !sanitizedValue || isPreview
                }
                isLoading={isSending}
                value="Send"
                onClick={onSendReply}
              />
            </ButtonGroup>
          </div>
        </footer>
      </Flex>
    </>
  );
};
